import {
  Loader2,
  RefreshCw,
  Search,
  ShoppingBasket,
  Store as StoreIcon,
  UtensilsCrossed,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useStorefronts } from "@/hooks/use-storefronts";
import { cn } from "@/lib/utils";
import type { Storefront, StorefrontBusinessType } from "@/types/storefront";

const SECTION_LABEL_KEY: Record<StorefrontBusinessType, string> = {
  restaurant: "storefront.discovery.sections.restaurants",
  supermarket: "storefront.discovery.sections.supermarkets",
};

const SECTION_LABEL_FALLBACK: Record<StorefrontBusinessType, string> = {
  restaurant: "Restaurantes",
  supermarket: "Supermercados",
};

const BUSINESS_TYPE_LABEL_KEY: Record<StorefrontBusinessType, string> = {
  restaurant: "storefront.discovery.businessType.restaurant",
  supermarket: "storefront.discovery.businessType.supermarket",
};

const BUSINESS_TYPE_LABEL_FALLBACK: Record<StorefrontBusinessType, string> = {
  restaurant: "Restaurante",
  supermarket: "Supermercado",
};

const SECTION_ORDER: StorefrontBusinessType[] = ["restaurant", "supermarket"];

const FALLBACK_GRADIENT: Record<StorefrontBusinessType, string> = {
  restaurant:
    "bg-gradient-to-br from-orange-100 via-amber-50 to-rose-100 dark:from-orange-950/60 dark:via-amber-950/40 dark:to-rose-950/60",
  supermarket:
    "bg-gradient-to-br from-emerald-100 via-teal-50 to-sky-100 dark:from-emerald-950/60 dark:via-teal-950/40 dark:to-sky-100/60",
};

const FALLBACK_ICON_TONE: Record<StorefrontBusinessType, string> = {
  restaurant: "text-orange-500/70 dark:text-orange-300/60",
  supermarket: "text-emerald-600/70 dark:text-emerald-300/60",
};

const FALLBACK_BADGE_TONE: Record<StorefrontBusinessType, string> = {
  restaurant: "bg-orange-500/10 text-orange-700 dark:text-orange-200",
  supermarket: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-200",
};

// All user-facing labels per business type across supported languages. The
// search filter checks these so a guest typing "restaurante" or "restaurant"
// narrows the list to the same group regardless of the active UI language.
export const BUSINESS_TYPE_SEARCH_ALIASES: Record<StorefrontBusinessType, readonly string[]> = {
  restaurant: ["restaurante", "restaurantes", "restaurant", "restaurants"],
  supermarket: ["supermercado", "supermercados", "supermarket", "supermarkets"],
};

export function filterStorefrontsByQuery(
  storefronts: readonly Storefront[],
  query: string,
): Storefront[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return [...storefronts];
  }
  return storefronts.filter((storefront) => {
    if (storefront.name.toLowerCase().includes(normalized)) {
      return true;
    }
    const aliases = BUSINESS_TYPE_SEARCH_ALIASES[storefront.businessType];
    return aliases.some((alias) => alias.toLowerCase().includes(normalized));
  });
}

function ctaCopyKey(businessType: StorefrontBusinessType): string {
  return businessType === "restaurant"
    ? "storefront.discovery.cta.viewMenu"
    : "storefront.discovery.cta.shop";
}

function ctaCopyFallback(businessType: StorefrontBusinessType): string {
  return businessType === "restaurant" ? "Ver menú" : "Comprar";
}

function menuPath(slug: string): string {
  return `/m/${slug}`;
}

function getBusinessTypeIcon(businessType: StorefrontBusinessType) {
  return businessType === "restaurant" ? UtensilsCrossed : ShoppingBasket;
}

function HeroFallback({ businessType }: { businessType: StorefrontBusinessType }) {
  const Icon = getBusinessTypeIcon(businessType);
  return (
    <div
      aria-hidden
      className={cn(
        "flex h-full w-full items-center justify-center",
        FALLBACK_GRADIENT[businessType],
      )}
    >
      <Icon className={cn("h-20 w-20", FALLBACK_ICON_TONE[businessType])} />
    </div>
  );
}

function StorefrontCard({ storefront }: { storefront: Storefront }) {
  const { t } = useTranslation();
  const heroAlt = t("storefront.discovery.heroAlt", { name: storefront.name });
  const logoAlt = t("storefront.discovery.logoAlt", { name: storefront.name });
  const ratingLabel = t("storefront.discovery.rating.noRatings", "Sin calificaciones");
  const businessTypeLabel = t(
    BUSINESS_TYPE_LABEL_KEY[storefront.businessType],
    BUSINESS_TYPE_LABEL_FALLBACK[storefront.businessType],
  );
  const isRestaurant = storefront.businessType === "restaurant";
  const CtaIcon = getBusinessTypeIcon(storefront.businessType);
  const ratingLabelId = `storefront-rating-${storefront.id}`;
  const businessTypeLabelId = `storefront-business-type-${storefront.id}`;

  return (
    <Link
      aria-label={storefront.name}
      aria-labelledby={`${ratingLabelId} ${businessTypeLabelId}`}
      className="group flex h-full flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm transition hover:-translate-y-0.5 hover:border-foreground/30 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-0"
      to={menuPath(storefront.slug)}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {storefront.heroImageUrl ? (
          <img
            alt={heroAlt}
            className="h-full w-full object-cover transition group-hover:scale-[1.02]"
            loading="lazy"
            src={storefront.heroImageUrl}
          />
        ) : (
          <HeroFallback businessType={storefront.businessType} />
        )}
        <span
          className={cn(
            "absolute top-3 left-3 rounded-full px-2.5 py-1 font-medium text-xs shadow-sm",
            FALLBACK_BADGE_TONE[storefront.businessType],
          )}
          id={businessTypeLabelId}
        >
          {businessTypeLabel}
        </span>
        <span
          aria-label={t("storefront.discovery.rating.noRatings", "Sin calificaciones")}
          className="absolute right-3 bottom-3 rounded-full bg-background/90 px-2.5 py-1 font-medium text-foreground text-xs shadow-sm"
          id={ratingLabelId}
        >
          {ratingLabel}
        </span>
      </div>
      <div className="flex flex-1 flex-col gap-3 px-4 py-4 sm:px-5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted">
            {storefront.logo ? (
              <img
                alt={logoAlt}
                className="h-full w-full object-cover"
                loading="lazy"
                src={storefront.logo}
              />
            ) : (
              <span aria-hidden className="font-semibold text-muted-foreground text-xs">
                {t("storefront.discovery.logoPlaceholder", "Logo")}
              </span>
            )}
          </div>
          <h3 className="line-clamp-2 font-semibold text-base">{storefront.name}</h3>
        </div>
        <Button
          aria-hidden
          className="pointer-events-none mt-auto h-10 w-full text-sm font-medium"
          size="sm"
          tabIndex={-1}
          variant={isRestaurant ? "outline" : "default"}
        >
          <CtaIcon className="mr-1.5 h-4 w-4" />
          {t(ctaCopyKey(storefront.businessType), ctaCopyFallback(storefront.businessType))}
        </Button>
      </div>
    </Link>
  );
}

function StorefrontGrid({ storefronts }: { storefronts: Storefront[] }) {
  const { t } = useTranslation();

  const grouped = SECTION_ORDER.map((businessType) => ({
    businessType,
    items: storefronts.filter((storefront) => storefront.businessType === businessType),
  })).filter((group) => group.items.length > 0);

  return (
    <div className="flex flex-col gap-10">
      {grouped.map((group) => (
        <section
          aria-label={t(
            SECTION_LABEL_KEY[group.businessType],
            SECTION_LABEL_FALLBACK[group.businessType],
          )}
          className="flex flex-col gap-4"
          key={group.businessType}
        >
          <div className="flex items-baseline justify-between gap-3">
            <h2 className="font-semibold text-2xl tracking-tight">
              {t(SECTION_LABEL_KEY[group.businessType], SECTION_LABEL_FALLBACK[group.businessType])}
            </h2>
            <span className="font-medium text-muted-foreground text-xs">
              {group.items.length === 1
                ? t("storefront.discovery.sectionCountOne", {
                    count: group.items.length,
                    defaultValue: "{count} local",
                  })
                : t("storefront.discovery.sectionCount", {
                    count: group.items.length,
                    defaultValue: "{count} locales",
                  })}
            </span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {group.items.map((storefront) => (
              <StorefrontCard key={storefront.id} storefront={storefront} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function NoResultsState({ query, onClear }: { query: string; onClear: () => void }) {
  const { t } = useTranslation();
  const title = t("storefront.discovery.noResultsTitle", {
    defaultValue: "No matches for “{query}”",
    query,
  });
  return (
    <section
      aria-label={t("storefront.discovery.noResultsLabel", "No matches")}
      className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center"
    >
      <Search aria-hidden className="h-10 w-10 text-muted-foreground" />
      <h2 className="font-semibold text-xl">{title}</h2>
      <p className="max-w-md text-muted-foreground text-sm">
        {t(
          "storefront.discovery.noResultsDescription",
          "Try another name or clear the filter to see all available places.",
        )}
      </p>
      <Button onClick={onClear} size="sm" type="button" variant="outline">
        {t("storefront.discovery.clearSearch", "Clear search")}
      </Button>
    </section>
  );
}

export default function StorefrontDiscovery() {
  const { t } = useTranslation();
  const { data, isError, isPending, refetch } = useStorefronts();
  const [searchTerm, setSearchTerm] = useState("");

  const storefronts = data?.storefronts ?? [];
  const normalizedTerm = searchTerm.trim();
  const filteredStorefronts = useMemo(
    () => filterStorefrontsByQuery(storefronts, normalizedTerm),
    [storefronts, normalizedTerm],
  );

  const searchDisabled = isPending || isError || storefronts.length === 0;
  const showLoading = isPending;
  const showError = !isPending && isError;
  const showGlobalEmpty = !isPending && !isError && storefronts.length === 0;
  const showNoResults =
    !isPending && !isError && storefronts.length > 0 && filteredStorefronts.length === 0;
  const showGrid =
    !isPending && !isError && storefronts.length > 0 && filteredStorefronts.length > 0;

  return (
    <div className="flex flex-col gap-10 sm:gap-12">
      <section className="flex flex-col items-center gap-4 rounded-2xl border bg-gradient-to-b from-background via-background to-muted/40 px-4 py-8 text-center sm:gap-5 sm:px-8 sm:py-10">
        <h1 className="font-bold text-3xl tracking-tight sm:text-4xl md:text-5xl">
          {t("storefront.discovery.title", "Pedí a tus locales favoritos")}
        </h1>
        <p className="mx-auto max-w-2xl text-base text-muted-foreground sm:text-lg">
          {t(
            "storefront.discovery.subtitle",
            "Restaurantes y supermercados disponibles para ordenar en minutos.",
          )}
        </p>
        <div className="mt-2 w-full max-w-xl">
          <label className="sr-only" htmlFor="storefront-search">
            {t("storefront.discovery.searchLabel", "Buscar locales")}
          </label>
          <div className="relative">
            <Search
              aria-hidden
              className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground"
            />
            <Input
              className="h-11 pl-9 text-base shadow-sm sm:text-base"
              disabled={searchDisabled}
              id="storefront-search"
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={t(
                "storefront.discovery.searchPlaceholder",
                "Buscar restaurantes o supermercados",
              )}
              type="search"
              value={searchTerm}
            />
          </div>
          <p className="mt-2 text-muted-foreground text-xs">
            {t(
              "storefront.discovery.searchHint",
              "La búsqueda se habilita cuando los locales estén disponibles.",
            )}
          </p>
        </div>
      </section>

      {showLoading ? (
        <section
          aria-busy
          aria-label={t("storefront.discovery.loadingLabel", "Cargando locales disponibles")}
          className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center"
        >
          <Loader2 aria-hidden className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground text-sm">
            {t("storefront.discovery.loadingLabel", "Cargando locales disponibles")}
          </p>
        </section>
      ) : showError ? (
        <section
          aria-live="polite"
          className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center"
        >
          <StoreIcon aria-hidden className="h-10 w-10 text-muted-foreground" />
          <h2 className="font-semibold text-xl">
            {t("storefront.discovery.errorTitle", "No pudimos cargar los locales")}
          </h2>
          <p className="max-w-md text-muted-foreground text-sm">
            {t(
              "storefront.discovery.errorDescription",
              "Revisá tu conexión y volvé a intentarlo en unos segundos.",
            )}
          </p>
          <Button
            onClick={() => {
              refetch();
            }}
            size="sm"
            type="button"
            variant="outline"
          >
            <RefreshCw aria-hidden className="mr-1.5 h-4 w-4" />
            {t("storefront.discovery.retry", "Reintentar")}
          </Button>
        </section>
      ) : showGlobalEmpty ? (
        <section
          aria-label={t("storefront.discovery.emptyLabel", "Locales disponibles")}
          className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-16 text-center"
        >
          <StoreIcon aria-hidden className="h-10 w-10 text-muted-foreground" />
          <h2 className="font-semibold text-xl">
            {t("storefront.discovery.emptyTitle", "Pronto vas a ver los locales disponibles")}
          </h2>
          <p className="max-w-md text-muted-foreground text-sm">
            {t(
              "storefront.discovery.emptyDescription",
              "Estamos preparando la lista inicial de restaurantes y supermercados para que puedas empezar a pedir.",
            )}
          </p>
        </section>
      ) : showNoResults ? (
        <NoResultsState onClear={() => setSearchTerm("")} query={normalizedTerm} />
      ) : null}

      {showGrid ? <StorefrontGrid storefronts={filteredStorefronts} /> : null}
    </div>
  );
}
